import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { Globe, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { STAFF_PROXY_TESTING_DEVICE_MDM_DETAILS } from '@staff-app/graphql-client';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import { errorText, parseTimezoneReply, timezoneOptions } from '../lib/device-commands';
import { TestingDialogFrame, type TestingCommandDialogProps } from './TestingDialogFrame';
import { useDrawerScopeVars } from '../../../lib/drawer-mode';

/**
 * More › Update time zone — port of scio3 `TimezoneDialog`: read the device's zone
 * (mdmDetails GET_TIMEZONE), pick an IANA zone, send mdmCommand SET_TIMEZONE with it.
 */
export function TestingTimezoneDialog({
  open,
  detail,
  commands,
  onClose,
  onDone,
}: TestingCommandDialogProps) {
  const scope = useDrawerScopeVars();
  const { data, loading } = useQuery<{ staffProxyTestingDeviceMdmDetails: string | null }>(
    STAFF_PROXY_TESTING_DEVICE_MDM_DETAILS,
    {
      variables: { deviceId: detail._id, command: 'GET_TIMEZONE', fresh: true, ...scope },
      skip: !open,
      fetchPolicy: 'network-only',
    },
  );
  const current = parseTimezoneReply(data?.staffProxyTestingDeviceMdmDetails) ?? detail.tz ?? null;
  const [picked, setPicked] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [sending, setSending] = useState(false);

  const selected = picked ?? current ?? '';
  const zones = useMemo(() => {
    const all = timezoneOptions(current);
    const q = filter.trim().toLowerCase();
    const hits = q ? all.filter((z) => z.toLowerCase().includes(q)) : all;
    return selected && !hits.includes(selected) ? [selected, ...hits] : hits;
  }, [current, filter, selected]);

  const dirty = !!picked && picked !== current;

  const apply = async () => {
    if (!picked) return;
    setSending(true);
    try {
      await commands.mdmCommand('SET_TIMEZONE', picked);
      toast.success(`Timezone set to ${picked}`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(errorText(err, 'Failed to set timezone'));
    } finally {
      setSending(false);
    }
  };

  return (
    <TestingDialogFrame
      open={open}
      onClose={onClose}
      title={'Update time zone'}
      icon={Globe}
      testId="testing-timezone-dialog"
      footer={
        <Button
          onClick={() => void apply()}
          disabled={!dirty || sending}
          disabledTooltip={!dirty && !sending ? 'No changes to save' : undefined}
          data-testid="testing-timezone-apply"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
          {'Apply'}
        </Button>
      }
    >
      <p className="text-sm text-muted-foreground" data-testid="testing-timezone-current">
        {loading && !data
          ? 'Reading the device time zone…'
          : current
            ? `Device time zone: ${current}`
            : 'The device did not report its time zone.'}
      </p>
      <div className="space-y-2">
        <Label htmlFor="testing-timezone-filter">{'Filter'}</Label>
        <Input
          id="testing-timezone-filter"
          placeholder={'e.g. Chicago'}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="testing-timezone-select">{'Time zone'}</Label>
        <Select value={selected || undefined} onValueChange={setPicked}>
          <SelectTrigger id="testing-timezone-select" className="cursor-pointer">
            <SelectValue placeholder={'Select timezone…'} />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {zones.map((z) => (
              <SelectItem key={z} value={z}>
                {z}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </TestingDialogFrame>
  );
}
